import { Button, Drawer, Input, Space, Typography } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactSketchCanvas } from 'react-sketch-canvas';
import { useScreenBreakpoint } from 'hooks';

import type { ReactNode } from 'react';
import type { UploadFile } from 'antd/es/upload/interface';
import type { ReactSketchCanvasRef } from 'react-sketch-canvas';

const { Text } = Typography;

type AnnotationDrawerProps = {
  open: boolean;
  file?: UploadFile | null;
  imageSrc?: string;
  onCancel: () => void;
  onSave?: (dataUrl: string, fileName?: string) => Promise<void>;
  saving?: boolean;
  title?: ReactNode;
  defaultTitle?: string;
};

const COLOR_OPTIONS = [ '#000000', '#ff4d4f', '#1677ff', '#52c41a', '#faad14', '#722ed1', '#8c8c8c' ];
const DEFAULT_BRUSH_COLOR = '#000000';
const FIXED_LONG_EDGE = 900;
const FALLBACK_SIZE = { width: 900, height: 600 };

type CanvasMode = 'draw' | 'pan';

const AnnotationDrawer = ({
  open,
  file,
  imageSrc,
  onCancel,
  onSave,
  saving,
  defaultTitle,
}: AnnotationDrawerProps) => {
  const canvasRef = useRef<ReactSketchCanvasRef | null>(null);
  const { t } = useTranslation();
  const { isMobile } = useScreenBreakpoint();

  const fileName = file?.name;

  const imageSource =
    imageSrc ?? file?.url ?? (file?.preview as string | undefined);

  const [ brushColor, setBrushColor ] = useState(DEFAULT_BRUSH_COLOR);
  const [ canvasSize, setCanvasSize ] = useState<{ width: number; height: number } | null>(null);
  const [ attachmentTitle, setAttachmentTitle ] = useState('');

  const [ mode, setMode ] = useState<CanvasMode>('draw');

  useEffect(() => {
    if (!imageSource) {
      setCanvasSize(null);
      return;
    }

    const img = document.createElement('img');

    img.onload = () => {
      const { naturalWidth, naturalHeight } = img;
      if (!naturalWidth || !naturalHeight) {
        setCanvasSize(FALLBACK_SIZE);
        return;
      }

      let width: number;
      let height: number;

      if (naturalWidth >= naturalHeight) {
        width = FIXED_LONG_EDGE;
        height = Math.round(FIXED_LONG_EDGE * (naturalHeight / naturalWidth));
      } else {
        height = FIXED_LONG_EDGE;
        width = Math.round(FIXED_LONG_EDGE * (naturalWidth / naturalHeight));
      }

      setCanvasSize({ width, height });
    };
    img.src = imageSource;
  }, [ imageSource ]);

  useEffect(() => {
    if (open) {
      setBrushColor(DEFAULT_BRUSH_COLOR);
      setMode('draw');
    }
  }, [ open, imageSource ]);

  const stripExtension = (name: string) => name.replace(/\.[^.]+$/, '');

  useEffect(() => {
    if (open) {
      const fallbackName = defaultTitle ?? fileName ?? '';
      const baseTitle = stripExtension(fallbackName).trim();
      setAttachmentTitle(baseTitle || 'attachment');
    }
  }, [ open, fileName, defaultTitle ]);

  useEffect(() => {
    if (!open && canvasRef.current) {
      canvasRef.current.resetCanvas();
    }
  }, [ open ]);

  useEffect(() => {
    if (open && canvasRef.current) {
      canvasRef.current.resetCanvas();
    }
  }, [ open, file?.uid, imageSource ]);

  const annotatableImage = imageSource;

  const handleSave = async () => {
    const trimmedTitle = attachmentTitle.trim();
    if (!onSave || !canvasRef.current || !trimmedTitle) return;
    const dataUrl = await canvasRef.current.exportImage('png');
    await onSave(dataUrl, trimmedTitle);
  };

  const handleUndo = () => canvasRef.current?.undo?.();
  const handleClear = () => canvasRef.current?.clearCanvas?.();

  const canAnnotate = !!onSave && !!annotatableImage;

  const width = canvasSize?.width ?? FALLBACK_SIZE.width;
  const height = canvasSize?.height ?? FALLBACK_SIZE.height;

  const showPanControls = isMobile && canAnnotate;
  const isPanMode = showPanControls && mode === 'pan';
  const canvasPointerEvents = isPanMode ? 'none' : 'auto';
  const touchAction = isPanMode ? 'pan-x pan-y' : (showPanControls ? 'none' : 'auto');

  useEffect(() => {
    if (!showPanControls && mode !== 'draw') {
      setMode('draw');
    }
  }, [ showPanControls, mode ]);

  const footerContent = !onSave
    ? (
      <Space>
        <Button onClick={onCancel}>{t('common.close')}</Button>
      </Space>
    )
    : (
      <Space>
        <Button onClick={onCancel}>{t('common.cancel')}</Button>
        <Button
          type="primary"
          onClick={handleSave}
          loading={saving}
          disabled={saving || !attachmentTitle.trim()}
        >
          {t('common.save')}</Button>
      </Space>
    );

  return (
    <Drawer
      open={open}
      title={t('common.upload.annotate')}
      onClose={onCancel}
      placement="right"
      width="100%"
      destroyOnClose
      footer={footerContent}
      footerStyle={{ display: 'flex', justifyContent: 'flex-end' }}
      closable
      maskClosable={false}
      rootClassName="annotation-drawer"
    >
      {!annotatableImage && (
        <Text type="secondary">{t('common.nodata')}</Text>
      )}

      {annotatableImage && canAnnotate && (
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            <Text strong>{t('common.upload.attachmentTitle')}</Text>
            <Input
              value={attachmentTitle}
              onChange={(event) => setAttachmentTitle(event.target.value)}
              placeholder={t('common.upload.attachmentTitlePlaceholder')}
              maxLength={120}
              disabled={saving}
            />
          </Space>
          <Space wrap align="center">
            {showPanControls && (
              <>
                <Button
                  type={mode === 'draw' ? 'primary' : 'default'}
                  onClick={() => setMode('draw')}
                >
                  {t('common.upload.draw')}
                </Button>
                <Button
                  type={mode === 'pan' ? 'primary' : 'default'}
                  onClick={() => setMode('pan')}
                >
                  {t('common.upload.move')}
                </Button>
              </>
            )}

            <Button onClick={handleUndo} disabled={saving || isPanMode}>
              {t('common.upload.undo')}
            </Button>
            <Button onClick={handleClear} disabled={saving || isPanMode}>
              {t('common.upload.clear')}
            </Button>
            <Button
              className="annotation-color-button"
              style={{ color: brushColor, borderColor: brushColor }}
              disabled={isPanMode}
            >
              {t('common.upload.color')}
              <input
                className="annotation-color-input"
                type="color"
                value={brushColor}
                onChange={(event) => setBrushColor(event.target.value)}
                aria-label={t('common.upload.customColor')}
              />
            </Button>
            {COLOR_OPTIONS.map((color) => (
              <Button
                key={color}
                shape="circle"
                size="small"
                style={{
                  backgroundColor: color,
                  borderColor: color,
                  boxShadow: brushColor === color ? '0 0 0 2px #1677ff' : undefined,
                  width: 26,
                  height: 26,
                  padding: 0
                }}
                onClick={() => {
                  if (!isPanMode) setBrushColor(color);
                }}
                aria-label={`${t('common.upload.color')}: ${color}`}
              />
            ))}
          </Space>

          <div
            style={{
              overflow: isMobile ? 'auto' : 'visible',
              touchAction
            }}
          >
            <div style={{ pointerEvents: canvasPointerEvents }}>
              <ReactSketchCanvas
                className='canvas'
                ref={canvasRef}
                width={`${width}px`}
                height={`${height}px`}
                exportWithBackgroundImage
                backgroundImage={annotatableImage}
                strokeWidth={3}
                strokeColor={brushColor}
                preserveBackgroundImageAspectRatio="xMidYMid meet"
              />
            </div>
          </div>
        </Space>
      )}
    </Drawer>
  );
};

export default AnnotationDrawer;
